import { type ExternalToast, toast as sonnerToast } from "sonner";
import { recordError } from "@/hooks/error-log-store.ts";

/**
 * How long an error toast stays up. sonner's 4 s default suits a confirmation,
 * but an error usually carries the backend's reason (up to 300 characters via
 * `apiErrorText`) and was gone before it could be read. sonner pauses the timer
 * while the pointer is over the toast and while the tab is hidden.
 */
export const ERROR_TOAST_DURATION_MS = 10_000;
/** A warning asks for a decision more often than a success does, less than an error. */
export const WARNING_TOAST_DURATION_MS = 7_000;

type ToastMessage = Parameters<typeof sonnerToast.error>[0];

function error(message: ToastMessage, data?: ExternalToast): string | number {
	// Every call site passes text; a rendered node has no text to keep.
	if (typeof message === "string") {
		recordError({
			message,
			description:
				typeof data?.description === "string" ? data.description : undefined,
		});
	}
	// A ten-second toast needs a way out before the timer runs down.
	return sonnerToast.error(message, {
		duration: ERROR_TOAST_DURATION_MS,
		closeButton: true,
		...data,
	});
}

function warning(message: ToastMessage, data?: ExternalToast): string | number {
	return sonnerToast.warning(message, {
		duration: WARNING_TOAST_DURATION_MS,
		...data,
	});
}

/**
 * The app's one toast entry point: sonner's `toast` with longer defaults for
 * errors and warnings, and every error also kept in the session's error log
 * (the Activity Hub's Errors tab). A caller's explicit option still wins. Import
 * this, never `sonner` directly (Biome enforces it), so no error toast is missed.
 */
export const toast: typeof sonnerToast = Object.assign(
	(message: ToastMessage, data?: ExternalToast) => sonnerToast(message, data),
	sonnerToast,
	{ error, warning },
);
